import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Box, Tabs, Tab, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import BarcodeGeneratorPage from './BarcodeGeneratorPage';
import QrGeneratorPage from '../qrcode/QrGeneratorPage';
import axiosInstance from '../../services/axiosInstance';
import { IUser } from '../../types/damTypes';
import { hasPermission } from '../../utils/permissionUtils';

const mainTabSx = {
  fontWeight: 600, fontSize: '0.9rem', textTransform: 'none' as const,
  minHeight: 40, borderRadius: '8px', mx: 0.5, px: 2.5,
  '&.Mui-selected': { bgcolor: '#424242', color: '#fff' },
  '&:not(.Mui-selected)': { color: 'text.secondary', bgcolor: '#f0f0f0', '&:hover': { bgcolor: '#e0e0e0' } },
  transition: 'all 0.2s ease',
};

interface CategoryItem { _id: string; name: string; parentCategory?: { _id: string; name: string } | null; }
interface CategorySettings { ean: { mainCategoryId: string; subCategoryId: string }; qr: { mainCategoryId: string; subCategoryId: string }; }

const SETTINGS_KEY = 'code-generator-category-settings';
const defaultSettings: CategorySettings = { ean: { mainCategoryId: '', subCategoryId: '' }, qr: { mainCategoryId: '', subCategoryId: '' } };
function loadSettings(): CategorySettings {
  try {
    const raw = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
    return {
      ean: { ...defaultSettings.ean, ...raw.ean },
      qr: { ...defaultSettings.qr, ...raw.qr },
    };
  } catch { return { ...defaultSettings }; }
}
function saveSettings(s: CategorySettings) { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); }

interface CodeGeneratorProps {
  onClose: () => void;
  currentUser: IUser | null;
}

const CodeGeneratorModule: React.FC<CodeGeneratorProps> = ({ onClose, currentUser }) => {
  const [mainTab, setMainTab] = useState<'ean' | 'qr'>('ean');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [categorySettings, setCategorySettings] = useState<CategorySettings>(loadSettings);
  const [allCategories, setAllCategories] = useState<CategoryItem[]>([]);
  const canAccessBarcodeGenerator = hasPermission(currentUser, 'barcode:access');

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/categories');
      setAllCategories(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (!canAccessBarcodeGenerator) return;
    loadCategories();
  }, [canAccessBarcodeGenerator, loadCategories]);

  const mainCategories = allCategories.filter(c => !c.parentCategory);
  const getSubcategories = (parentId: string) =>
    allCategories.filter(c => {
      const pid = typeof c.parentCategory === 'object' ? c.parentCategory?._id : c.parentCategory;
      return pid === parentId;
    });

  const currentTabSettings = mainTab === 'ean' ? categorySettings.ean : categorySettings.qr;

  const handleSettingChange = (field: 'mainCategoryId' | 'subCategoryId', value: string) => {
    const updated = { ...categorySettings };
    const target = mainTab === 'ean' ? 'ean' : 'qr';
    updated[target] = { ...updated[target], [field]: value };
    if (field === 'mainCategoryId') updated[target].subCategoryId = '';
    setCategorySettings(updated);
    saveSettings(updated);
  };

  const categoryIds: string[] = [];
  if (currentTabSettings.mainCategoryId) categoryIds.push(currentTabSettings.mainCategoryId);
  if (currentTabSettings.subCategoryId) categoryIds.push(currentTabSettings.subCategoryId);
  const activeCategoryName = allCategories.find(c => c._id === (currentTabSettings.subCategoryId || currentTabSettings.mainCategoryId))?.name;

  if (!canAccessBarcodeGenerator) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: 560, width: '100%' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Du har inte behörighet att visa EAN-koder. Rollen behöver ha <strong>`barcode:access`</strong>.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={onClose}>
              Stäng
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 0.5,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
      }}>
        <IconButton onClick={onClose} sx={{ mr: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{ flex: 1, minHeight: 44, '& .MuiTabs-indicator': { display: 'none' } }}
        >
          <Tab label="EAN-kod" value="ean" sx={mainTabSx} />
          <Tab label="QR-kod" value="qr" sx={mainTabSx} />
        </Tabs>
        <Tooltip title={`Kategori-inställningar${activeCategoryName ? ` (${activeCategoryName})` : ''}`}>
          <IconButton onClick={() => setSettingsOpen(true)} size="small" sx={{ mr: 0.5 }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {mainTab === 'ean'
          ? <BarcodeGeneratorPage categoryIds={categoryIds} />
          : <QrGeneratorPage categoryIds={categoryIds} />
        }
      </Box>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Kategori-inställningar – {mainTab === 'ean' ? 'EAN-koder' : 'QR-koder'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Välj vilken kategori genererade koder ska sparas i. Lämna tomt för standardkategori.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Huvudkategori</InputLabel>
            <Select
              value={currentTabSettings.mainCategoryId}
              label="Huvudkategori"
              onChange={e => handleSettingChange('mainCategoryId', e.target.value)}
            >
              <MenuItem value="">Standard (auto)</MenuItem>
              {mainCategories.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          {currentTabSettings.mainCategoryId && getSubcategories(currentTabSettings.mainCategoryId).length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Underkategori</InputLabel>
              <Select
                value={currentTabSettings.subCategoryId}
                label="Underkategori"
                onChange={e => handleSettingChange('subCategoryId', e.target.value)}
              >
                <MenuItem value="">Ingen underkategori</MenuItem>
                {getSubcategories(currentTabSettings.mainCategoryId).map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {activeCategoryName && (
            <Typography variant="caption" color="text.secondary">
              Koder sparas i: <strong>{activeCategoryName}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodeGeneratorModule;
